import React, { useCallback, useState } from "react";

import { Checkbox, TextField } from "@mui/material";
import * as Styled from "./styles";
import { CatalogProductsProps } from "store/modules/Catalog/types.store";
import { formatProductNameAndDescription } from "utils";

type ProductCardProps = {
  product: CatalogProductsProps;
  onUpdate: (productId: string, productPrice: number) => void;
  edit?: boolean;
  hasDefinedPrice?: number;
};

// Formata centavos para exibição de moeda (ex: 12345 -> "R$ 123,45")
const formatCentsToDisplay = (cents: number): string => {
  const value = cents / 100;
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

// Converte valor em reais para centavos (ex: 123.45 -> 12345)
const realsToCents = (value: number): number => {
  return Math.round(value * 100);
};

const ProductCard = ({
  product,
  onUpdate,
  edit = false,
  hasDefinedPrice = 0,
}: ProductCardProps) => {
  const [checked, setChecked] = useState(false);
  // Armazena o valor em centavos para facilitar a manipulação
  const [centsValue, setCentsValue] = useState(0);

  const handleChangeChecked = () => {
    setChecked(!checked);
  };

  const handleUpdate = useCallback(
    (productId: string, productPrice: number) => {
      onUpdate(productId, productPrice);
    },
    [onUpdate],
  );

  const handleChangePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove tudo que não é número
    const onlyNumbers = e.target.value.replace(/\D/g, "");

    // Converte para número (em centavos)
    const newCentsValue = parseInt(onlyNumbers, 10) || 0;

    // Limita a um valor máximo razoável (ex: R$ 999.999,99)
    const limitedCents = Math.min(newCentsValue, 99999999);

    setCentsValue(limitedCents);

    // Converte centavos para reais antes de enviar
    const priceInReals = limitedCents / 100;
    handleUpdate(product?.id, priceInReals);
  };

  // Se hasDefinedPrice está definido, converte de reais para centavos para exibição
  const displayCents =
    hasDefinedPrice > 0 ? realsToCents(hasDefinedPrice) : centsValue;

  return (
    <Styled.Container>
      <Styled.ProductInfo>
        {edit && (
          <Checkbox
            color="primary"
            size="small"
            checked={checked}
            onChange={handleChangeChecked}
          />
        )}
        <Styled.ProductImage src={product?.imageUrl} alt={product?.name} />
        <Styled.ProductName>
          {formatProductNameAndDescription(product?.name, product?.description)}
        </Styled.ProductName>
      </Styled.ProductInfo>
      <TextField
        disabled={!checked}
        error={undefined}
        name={product?.name}
        size="small"
        label="Preço"
        value={formatCentsToDisplay(displayCents)}
        inputProps={{ maxLength: 17, inputMode: "numeric" }}
        onChange={handleChangePrice}
      />
    </Styled.Container>
  );
};

export default ProductCard;
